import { Eyebrow } from "../atoms/Eyebrow";
import { Reveal } from "../atoms/Reveal";
import type { Copy, PmsEntry } from "../lib/copy";
import { PMS_LIST } from "../lib/copy";

type IntegrationsProps = { t: Copy };

/**
 * Two counter-rotating marquee rows of PMS/POS/channel chips. First row is
 * slices 0..10 of PMS_LIST, second row slices 11..21. Each row is duplicated
 * inline so the CSS animation can translate by -50% for a seamless loop.
 */
export function Integrations({ t }: IntegrationsProps) {
  const rowA = PMS_LIST.slice(0, 11);
  const rowB = PMS_LIST.slice(11);
  return (
    <section id="integrations" className="section integrations">
      <div className="container">
        <div className="section-head wide">
          <Reveal>
            <Eyebrow>{t.integrations.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal as="h2" className="section-title" delay={60}>
            <span className="ink">{t.integrations.title_a}</span>{" "}
            <span className="serif-accent">{t.integrations.title_b}</span>
          </Reveal>
          <Reveal as="p" className="section-sub" delay={120}>
            {t.integrations.sub}
          </Reveal>
        </div>
      </div>

      <div className="marquee" aria-hidden="true">
        <div className="marquee-track">
          {[...rowA, ...rowA].map((p, i) => (
            <LogoChip key={"a" + i} p={p} />
          ))}
        </div>
      </div>
      <div className="marquee reverse" aria-hidden="true">
        <div className="marquee-track">
          {[...rowB, ...rowB].map((p, i) => (
            <LogoChip key={"b" + i} p={p} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LogoChip({ p }: { p: PmsEntry }) {
  // Logo-only chip: just the brand mark, name lives in alt text for a11y.
  if (p.logo) {
    return (
      <div className="logo-chip logo-only" title={p.name}>
        <div className="lc-logo">
          <img src={p.logo} alt={p.name} loading="lazy" decoding="async" />
        </div>
      </div>
    );
  }
  // Fallback (no logo): keep the original initials + name + cat layout.
  const initials =
    p.name.replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase() || p.name.slice(0, 2);
  return (
    <div className="logo-chip">
      <div className="lc-mark" aria-hidden="true">
        <span>{initials}</span>
      </div>
      <div className="lc-meta">
        <div className="lc-name">{p.name}</div>
        <div className="lc-cat">{p.cat}</div>
      </div>
    </div>
  );
}
